import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { App as CapApp } from '@capacitor/app'
import {
  getProfissionalAgendamentos,
  getProfissionalAgendamentosSemana,
  getProfissionalMe,
  getProfissionalNotificacoes,
  getProfissionalNotificacoesUnreadCount,
  getProfissionalGoogleStatus,
  getProfissionalGoogleConnectUrl,
  disconnectProfissionalGoogle,
  marcarProfissionalNotificacaoLida,
  marcarTodasProfissionalNotificacoesLidas,
  updateProfissionalAgendamentoStatus,
  updateProfissionalMe,
  updateProfissionalPassword,
  type GoogleCalendarStatus,
  type ProfissionalAgendamento,
  type ProfissionalAgendamentoPeriod,
  type ProfissionalMe,
  type ProfissionalNotificacao,
  type ProfissionalSemanaDia,
} from '@/api/profissionalApi'
import { logoutProfissional } from '@/utils/profissionalAuth'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { hojeStr } from '@/utils/date'
import { CONTEUDO_CHAVES } from '@/api/adminApi'
import { useConteudoFlag } from '@/utils/featureFlags'
import styles from './Profissional.module.css'

type Tab = 'proximos' | 'passados' | 'semana'

function formatarData(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  if (!y || !m || !d) return dateStr
  return `${d}/${m}/${y}`
}

function diaSemanaCurto(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`)
  return d.toLocaleDateString('pt-PT', { weekday: 'short' })
}

function proximos7Dias(): string[] {
  const hoje = hojeStr()
  const [y, m, d] = hoje.split('-').map(Number)
  const base = new Date(y, m - 1, d)
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(base)
    dt.setDate(base.getDate() + i)
    const yy = dt.getFullYear()
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    const dd = String(dt.getDate()).padStart(2, '0')
    return `${yy}-${mm}-${dd}`
  })
}

function iniciais(nome?: string | null): string {
  const parts = String(nome || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  const a = parts[0][0] || ''
  const b = parts.length > 1 ? parts[parts.length - 1][0] || '' : parts[0][1] || ''
  return `${a}${b}`.toUpperCase()
}

function primeiroNome(nome?: string | null): string {
  return String(nome || '').trim().split(/\s+/).filter(Boolean)[0] || 'olá'
}

function statusLabel(status: string): string {
  if (status === 'agendado') return 'Agendado'
  if (status === 'realizado') return 'Realizado'
  if (status === 'cancelado') return 'Cancelado'
  return status
}

export function ProfissionalDashboardPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enabled: tarefasEnabled } = useConteudoFlag(CONTEUDO_CHAVES.tarefas_enabled)
  const [me, setMe] = useState<ProfissionalMe | null>(null)
  const [agendamentos, setAgendamentos] = useState<ProfissionalAgendamento[]>([])
  const [semanaCounts, setSemanaCounts] = useState<ProfissionalSemanaDia[]>([])
  const [notificacoes, setNotificacoes] = useState<ProfissionalNotificacao[]>([])
  const [notifCount, setNotifCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [tab, setTab] = useState<Tab>('proximos')
  const [diaSemana, setDiaSemana] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [cancelTarget, setCancelTarget] = useState<ProfissionalAgendamento | null>(null)
  const [cancelMotivo, setCancelMotivo] = useState('')
  const [editPerfil, setEditPerfil] = useState(false)
  const [perfilNome, setPerfilNome] = useState('')
  const [perfilEsp, setPerfilEsp] = useState('')
  const [savingPerfil, setSavingPerfil] = useState(false)
  const [showSenha, setShowSenha] = useState(false)
  const [senhaAtual, setSenhaAtual] = useState('')
  const [senhaNova, setSenhaNova] = useState('')
  const [senhaConf, setSenhaConf] = useState('')
  const [savingSenha, setSavingSenha] = useState(false)
  const [googleStatus, setGoogleStatus] = useState<GoogleCalendarStatus | null>(null)
  const [googleBusy, setGoogleBusy] = useState(false)
  const { notifySaved, notifyError, notifyInfo } = useSaveFeedback()

  const periodQuery: ProfissionalAgendamentoPeriod = useMemo(() => {
    if (tab === 'semana' && diaSemana) return diaSemana
    return tab
  }, [tab, diaSemana])

  const loadCore = useCallback(async (period: ProfissionalAgendamentoPeriod, silent = false) => {
    if (!silent) setErro(null)
    try {
      const [meData, ags, notifs, unread, semana, gStatus] = await Promise.all([
        getProfissionalMe(),
        getProfissionalAgendamentos(120, period),
        getProfissionalNotificacoes(20),
        getProfissionalNotificacoesUnreadCount(),
        getProfissionalAgendamentosSemana(),
        getProfissionalGoogleStatus().catch(() => null),
      ])
      setMe(meData)
      setAgendamentos(ags)
      setNotificacoes(notifs)
      setNotifCount(unread)
      setSemanaCounts(semana)
      setPerfilNome(meData.nome || '')
      setPerfilEsp(meData.especialidade || '')
      if (gStatus) setGoogleStatus(gStatus)
    } catch (e) {
      if (!silent) setErro(e instanceof Error ? e.message : 'Erro ao carregar painel do profissional')
    }
  }, [])

  useEffect(() => {
    loadCore(periodQuery, false).finally(() => setLoading(false))
  }, [periodQuery, loadCore])

  useEffect(() => {
    const resetGoogleBusy = () => setGoogleBusy(false)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        resetGoogleBusy()
        getProfissionalGoogleStatus()
          .then(setGoogleStatus)
          .catch(() => {})
      }
    }
    window.addEventListener('pageshow', resetGoogleBusy)
    window.addEventListener('popstate', resetGoogleBusy)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pageshow', resetGoogleBusy)
      window.removeEventListener('popstate', resetGoogleBusy)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let handle: { remove: () => Promise<void> } | undefined
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return
      setGoogleBusy(false)
      getProfissionalGoogleStatus()
        .then(setGoogleStatus)
        .catch(() => {})
      void Browser.close().catch(() => {})
    }).then((h) => {
      handle = h
    })
    return () => {
      void handle?.remove()
    }
  }, [])

  useEffect(() => {
    const g = searchParams.get('google')
    if (!g) return
    setGoogleBusy(false)
    if (g === 'ok') notifySaved('Google Calendar ligado com sucesso.')
    else {
      const msg = searchParams.get('google_msg')
      notifyError(new Error(msg || 'Não foi possível ligar o Google Calendar.'), 'Google Calendar')
    }
    const next = new URLSearchParams(searchParams)
    next.delete('google')
    next.delete('google_msg')
    setSearchParams(next, { replace: true })
    getProfissionalGoogleStatus()
      .then(setGoogleStatus)
      .catch(() => {})
  }, [searchParams, setSearchParams, notifySaved, notifyError])

  useEffect(() => {
    const id = window.setInterval(() => {
      loadCore(periodQuery, true)
    }, 60_000)
    return () => window.clearInterval(id)
  }, [periodQuery, loadCore])

  useEffect(() => {
    if (tab !== 'semana') setDiaSemana(null)
  }, [tab])

  const countsMap = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of semanaCounts) m.set(s.data, Number(s.total))
    return m
  }, [semanaCounts])

  const semanaTotal = useMemo(
    () => semanaCounts.reduce((acc, s) => acc + (Number(s.total) || 0), 0),
    [semanaCounts],
  )

  const statusClass = (status: string) => {
    if (status === 'realizado') return styles.statusOk
    if (status === 'cancelado') return styles.statusNo
    return styles.statusWait
  }

  const onTab = (t: Tab) => {
    setTab(t)
    setExpandedId(null)
  }

  const onStatus = async (id: number, status: 'realizado' | 'cancelado', motivo?: string) => {
    setSavingId(id)
    try {
      await updateProfissionalAgendamentoStatus(id, status, motivo)
      await loadCore(periodQuery, true)
      notifySaved(status === 'realizado' ? 'Marcado como realizado.' : 'Agendamento cancelado.')
      setCancelTarget(null)
      setCancelMotivo('')
    } catch (e) {
      notifyError(e, 'Erro ao atualizar status')
      setErro(e instanceof Error ? e.message : 'Erro ao atualizar status')
    } finally {
      setSavingId(null)
    }
  }

  const confirmCancel = () => {
    if (!cancelTarget) return
    onStatus(cancelTarget.id, 'cancelado', cancelMotivo)
  }

  const onLida = async (id: number) => {
    try {
      await marcarProfissionalNotificacaoLida(id)
      setNotificacoes((prev) => prev.map((n) => (n.id === id ? { ...n, lida: 1 } : n)))
      setNotifCount((c) => Math.max(0, c - 1))
    } catch {
      // sem bloqueio de fluxo
    }
  }

  const onMarcarTodasLidas = async () => {
    try {
      await marcarTodasProfissionalNotificacoesLidas()
      setNotificacoes((prev) => prev.map((n) => ({ ...n, lida: 1 })))
      setNotifCount(0)
      notifySaved('Todas as notificações marcadas como lidas.')
    } catch (e) {
      notifyError(e, 'Erro ao marcar notificações')
    }
  }

  const ligarGoogle = async () => {
    setGoogleBusy(true)
    try {
      const url = await getProfissionalGoogleConnectUrl()
      // No Capacitor, OAuth no WebView falha; abrir browser do sistema.
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url, presentationStyle: 'popover' })
        notifySaved('Conclua o login Google no browser e volte à app.')
        setGoogleBusy(false)
      } else {
        window.location.assign(url)
        window.setTimeout(() => setGoogleBusy(false), 2500)
      }
    } catch (e) {
      notifyError(e, 'Não foi possível iniciar a ligação Google')
      setGoogleBusy(false)
    }
  }

  const desligarGoogle = async () => {
    setGoogleBusy(true)
    try {
      await disconnectProfissionalGoogle()
      setGoogleStatus((s) => ({
        connected: false,
        email: null,
        configured: s?.configured ?? true,
      }))
      notifySaved('Google Calendar desligado.')
    } catch (e) {
      notifyError(e, 'Erro ao desligar Google Calendar')
    } finally {
      setGoogleBusy(false)
    }
  }

  const salvarPerfil = async () => {
    setSavingPerfil(true)
    try {
      const updated = await updateProfissionalMe({
        nome: perfilNome.trim(),
        especialidade: perfilEsp.trim(),
      })
      setMe(updated)
      setEditPerfil(false)
      notifySaved('Perfil actualizado.')
    } catch (e) {
      notifyError(e, 'Erro ao guardar perfil')
    } finally {
      setSavingPerfil(false)
    }
  }

  const salvarSenha = async () => {
    if (senhaNova.length < 6) {
      notifyError(new Error('Nova senha deve ter pelo menos 6 caracteres.'), 'Senha inválida')
      return
    }
    if (senhaNova !== senhaConf) {
      notifyError(new Error('Confirmação não coincide.'), 'Senha inválida')
      return
    }
    setSavingSenha(true)
    try {
      await updateProfissionalPassword(senhaAtual, senhaNova)
      setSenhaAtual('')
      setSenhaNova('')
      setSenhaConf('')
      setShowSenha(false)
      notifySaved('Senha alterada com sucesso.')
    } catch (e) {
      notifyError(e, 'Erro ao alterar senha')
    } finally {
      setSavingSenha(false)
    }
  }

  const sair = () => {
    logoutProfissional()
    notifyInfo('Sessão terminada.')
    navigate('/profissional', { replace: true })
  }

  const tituloLista =
    tab === 'proximos'
      ? 'Próximos atendimentos'
      : tab === 'passados'
        ? 'Atendimentos passados'
        : diaSemana
          ? `Dia ${formatarData(diaSemana)}`
          : 'Esta semana'

  if (loading) {
    return (
      <main className={styles.panel}>
        <div className={styles.panelWrap}>
          <div className={styles.skeletonHero} />
          <div className={styles.skeletonGrid}>
            <div className={styles.skeletonCard} />
            <div className={styles.skeletonCard} />
          </div>
        </div>
      </main>
    )
  }

  const googleLabel = googleStatus?.connected
    ? 'Ligado'
    : googleStatus?.configured === false
      ? 'Indisponível'
      : 'Por ligar'

  return (
    <main className={styles.panel}>
      <div className={styles.panelWrap}>
        <header className={styles.hero}>
          <div className={styles.heroLeft}>
            <div className={styles.avatar} aria-hidden>
              {iniciais(me?.nome)}
            </div>
            <div>
              <p className={styles.heroKicker}>Painel do profissional</p>
              <h1 className={styles.panelTitle}>Olá, {primeiroNome(me?.nome)}</h1>
              <p className={styles.heroMeta}>
                {[me?.especialidade, me?.unidade_nome || 'Todas as unidades'].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
          <div className={styles.heroActions}>
            {tarefasEnabled && (
              <button
                type="button"
                className={styles.logoutBtn}
                onClick={() => navigate('/profissional/tarefas')}
              >
                Tarefas
              </button>
            )}
            <button type="button" className={styles.logoutBtn} onClick={sair}>
              Sair
            </button>
          </div>
        </header>
        {erro && <p className={styles.error}>{erro}</p>}

        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Esta semana</span>
            <span className={styles.statValue}>{semanaTotal}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Não lidas</span>
            <span className={styles.statValue}>{notifCount}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Google Calendar</span>
            <span className={`${styles.statValue} ${styles.statValueSm}`}>{googleLabel}</span>
          </div>
        </div>

        <div className={styles.grid}>
          <section className={styles.box}>
            <div className={styles.boxHead}>
              <h2 className={styles.boxTitle}>Perfil</h2>
              {!editPerfil ? (
                <button type="button" className={styles.linkBtn} onClick={() => setEditPerfil(true)}>
                  Editar
                </button>
              ) : null}
            </div>

            {editPerfil ? (
              <div className={styles.formStack}>
                <label className={styles.label}>
                  Nome
                  <input
                    className={styles.input}
                    value={perfilNome}
                    onChange={(e) => setPerfilNome(e.target.value)}
                  />
                </label>
                <label className={styles.label}>
                  Especialidade
                  <input
                    className={styles.input}
                    value={perfilEsp}
                    onChange={(e) => setPerfilEsp(e.target.value)}
                  />
                </label>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.btnDone}
                    onClick={salvarPerfil}
                    disabled={savingPerfil}
                  >
                    Guardar
                  </button>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => {
                      setEditPerfil(false)
                      setPerfilNome(me?.nome || '')
                      setPerfilEsp(me?.especialidade || '')
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.profileRows}>
                <div className={styles.profileRow}>
                  <span className={styles.profileKey}>Nome</span>
                  <span className={styles.profileVal}>{me?.nome || '—'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileKey}>Especialidade</span>
                  <span className={styles.profileVal}>{me?.especialidade || '—'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileKey}>E-mail</span>
                  <span className={styles.profileVal}>{me?.email || '—'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileKey}>Unidade</span>
                  <span className={styles.profileVal}>{me?.unidade_nome || 'Todas'}</span>
                </div>
              </div>
            )}

            <div className={styles.googleBlock}>
              <div className={styles.googleHead}>
                <h3 className={styles.googleTitle}>Google Calendar</h3>
                <span
                  className={`${styles.badge} ${googleStatus?.connected ? styles.badgeOn : styles.badgeOff}`}
                >
                  {googleLabel}
                </span>
              </div>
              {googleStatus?.configured === false ? (
                <p className={styles.meta}>
                  Integração não disponível neste servidor (falta configuração Google).
                </p>
              ) : googleStatus?.connected ? (
                <>
                  <p className={styles.meta}>
                    Ligado{googleStatus.email ? ` como ${googleStatus.email}` : ''}. Novos agendamentos
                    entram na sua agenda e o cliente recebe convite por e-mail.
                  </p>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={desligarGoogle}
                    disabled={googleBusy}
                  >
                    {googleBusy ? 'A desligar…' : 'Desligar Google Calendar'}
                  </button>
                </>
              ) : (
                <>
                  <p className={styles.meta}>
                    Ligue a sua conta Google para receber marcações directamente na agenda.
                  </p>
                  <button
                    type="button"
                    className={styles.btnDone}
                    onClick={ligarGoogle}
                    disabled={googleBusy}
                  >
                    {googleBusy ? 'A redirecionar…' : 'Ligar Google Calendar'}
                  </button>
                </>
              )}
            </div>

            <button type="button" className={styles.linkBtn} onClick={() => setShowSenha((v) => !v)}>
              {showSenha ? 'Ocultar alterar senha' : 'Alterar senha'}
            </button>
            {showSenha && (
              <div className={styles.formStack}>
                <label className={styles.label}>
                  Senha actual
                  <input
                    type="password"
                    className={styles.input}
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    autoComplete="current-password"
                  />
                </label>
                <label className={styles.label}>
                  Nova senha
                  <input
                    type="password"
                    className={styles.input}
                    value={senhaNova}
                    onChange={(e) => setSenhaNova(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <label className={styles.label}>
                  Confirmar nova senha
                  <input
                    type="password"
                    className={styles.input}
                    value={senhaConf}
                    onChange={(e) => setSenhaConf(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={salvarSenha}
                  disabled={savingSenha}
                >
                  Actualizar senha
                </button>
              </div>
            )}
          </section>

          <section className={styles.box}>
            <div className={styles.tabs}>
              <button
                type="button"
                className={tab === 'proximos' ? styles.tabActive : styles.tab}
                onClick={() => onTab('proximos')}
              >
                Próximos
              </button>
              <button
                type="button"
                className={tab === 'passados' ? styles.tabActive : styles.tab}
                onClick={() => onTab('passados')}
              >
                Passados
              </button>
              <button
                type="button"
                className={tab === 'semana' ? styles.tabActive : styles.tab}
                onClick={() => onTab('semana')}
              >
                Semana
              </button>
            </div>

            {tab === 'semana' && (
              <div className={styles.weekStrip}>
                {proximos7Dias().map((dia) => {
                  const total = countsMap.get(dia) || 0
                  const active = diaSemana === dia
                  const isHoje = dia === hojeStr()
                  return (
                    <button
                      key={dia}
                      type="button"
                      className={`${styles.weekDay} ${active ? styles.weekDayActive : ''} ${isHoje ? styles.weekDayToday : ''}`}
                      onClick={() => setDiaSemana((prev) => (prev === dia ? null : dia))}
                    >
                      <span className={styles.weekDayName}>{diaSemanaCurto(dia)}</span>
                      <span className={styles.weekDayNum}>{dia.slice(8, 10)}</span>
                      {total > 0 && <span className={styles.weekDayBadge}>{total}</span>}
                    </button>
                  )
                })}
              </div>
            )}

            <h2 className={styles.boxTitle}>{tituloLista}</h2>
            {agendamentos.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon} aria-hidden />
                <p className={styles.meta}>Sem agendamentos neste período.</p>
              </div>
            ) : (
              <div className={styles.list}>
                {agendamentos.map((ag) => {
                  const expanded = expandedId === ag.id
                  const cliente = ag.cliente_nome || `Cliente #${ag.usuario_id}`
                  return (
                    <article key={ag.id} className={styles.item}>
                      <button
                        type="button"
                        className={styles.itemHead}
                        onClick={() => setExpandedId((id) => (id === ag.id ? null : ag.id))}
                      >
                        <div className={styles.agItem}>
                          <div className={styles.agTime}>
                            <span className={styles.agHour}>{ag.hora}</span>
                            <span className={styles.agDate}>{formatarData(ag.data)}</span>
                          </div>
                          <div className={styles.agBody}>
                            <div className={styles.agTop}>
                              <div className={styles.itemTitle}>{cliente}</div>
                              <span className={`${styles.statusPill} ${statusClass(ag.status)}`}>
                                {statusLabel(ag.status)}
                              </span>
                            </div>
                            <div className={styles.meta}>
                              {ag.servico_nome || 'Serviço não indicado'} · {ag.unidade}
                            </div>
                          </div>
                        </div>
                      </button>

                      {expanded && (
                        <div className={styles.detail}>
                          <p className={styles.meta}>
                            <strong>Serviço:</strong> {ag.servico_nome || '—'}
                          </p>
                          <p className={styles.meta}>
                            <strong>Data/hora:</strong> {formatarData(ag.data)} às {ag.hora}
                          </p>
                          <p className={styles.meta}>
                            <strong>Cliente:</strong> {cliente}
                          </p>
                          {ag.cliente_telefone ? (
                            <p className={styles.meta}>
                              <strong>Telefone:</strong>{' '}
                              <a href={`tel:${ag.cliente_telefone}`}>{ag.cliente_telefone}</a>
                            </p>
                          ) : (
                            <p className={styles.meta}>
                              <strong>Telefone:</strong> não registado
                            </p>
                          )}
                          {ag.cliente_email && (
                            <p className={styles.meta}>
                              <strong>E-mail:</strong>{' '}
                              <a href={`mailto:${ag.cliente_email}`}>{ag.cliente_email}</a>
                            </p>
                          )}
                          {ag.observacao && (
                            <p className={styles.meta}>
                              <strong>Obs.:</strong> {ag.observacao}
                            </p>
                          )}
                        </div>
                      )}

                      {ag.status === 'agendado' && tab !== 'passados' && (
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.btnDone}
                            onClick={() => onStatus(ag.id, 'realizado')}
                            disabled={savingId === ag.id}
                          >
                            Marcar realizado
                          </button>
                          <button
                            type="button"
                            className={styles.btnCancel}
                            onClick={() => {
                              setCancelTarget(ag)
                              setCancelMotivo('')
                            }}
                            disabled={savingId === ag.id}
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </article>
                  )
                })}
              </div>
            )}
          </section>
        </div>

        <section className={styles.box}>
          <div className={styles.boxHead}>
            <h2 className={styles.boxTitle}>Notificações</h2>
            {notifCount > 0 && (
              <button type="button" className={styles.linkBtn} onClick={onMarcarTodasLidas}>
                Marcar todas lidas
              </button>
            )}
          </div>
          {notificacoes.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon} aria-hidden />
              <p className={styles.meta}>Sem notificações.</p>
            </div>
          ) : (
            <div className={styles.list}>
              {notificacoes.map((n) => (
                <article
                  key={n.id}
                  className={`${styles.item} ${n.lida ? '' : styles.itemUnread}`}
                  onClick={() => !n.lida && onLida(n.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !n.lida) onLida(n.id)
                  }}
                >
                  <div className={styles.notifItem}>
                    <span
                      className={`${styles.notifDot} ${n.lida ? '' : styles.notifDotOn}`}
                      aria-hidden
                    />
                    <div>
                      <div className={styles.itemTitle}>{n.titulo}</div>
                      <div className={styles.meta}>{n.mensagem}</div>
                      <div className={styles.meta}>{n.lida ? 'Lida' : 'Não lida · toque para marcar'}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {cancelTarget && (
        <div className={styles.modalBackdrop} role="presentation" onClick={() => setCancelTarget(null)}>
          <div
            className={styles.modal}
            role="dialog"
            aria-labelledby="cancel-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cancel-title" className={styles.modalTitle}>
              Cancelar agendamento?
            </h3>
            <p className={styles.meta}>
              {formatarData(cancelTarget.data)} às {cancelTarget.hora} ·{' '}
              {cancelTarget.cliente_nome || `Cliente #${cancelTarget.usuario_id}`}
            </p>
            <label className={styles.label}>
              Motivo (opcional)
              <textarea
                className={styles.textarea}
                rows={3}
                value={cancelMotivo}
                onChange={(e) => setCancelMotivo(e.target.value)}
                placeholder="Ex.: cliente pediu reagendamento"
              />
            </label>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={confirmCancel}
                disabled={savingId === cancelTarget.id}
              >
                Confirmar cancelamento
              </button>
              <button type="button" className={styles.btnGhost} onClick={() => setCancelTarget(null)}>
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
